import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { AccordionModule } from 'primeng/accordion';
import { MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { ChipModule } from 'primeng/chip';
import { ContextMenuModule } from 'primeng/contextmenu';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { FieldsetModule } from 'primeng/fieldset';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { PanelModule } from 'primeng/panel';
import { SelectModule } from 'primeng/select';
import { Table, TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TreeSelectModule } from 'primeng/treeselect';
import { TreeTableModule } from 'primeng/treetable';

import { firstValueFrom } from 'rxjs';

import { SETTINGS, Settings } from '../../../../app.settings';

import { notifyAndTrackError } from '../../../../common/error';
import { successMsg } from '../../../../common/messages';
import { catalogInsectGrowthStageSort, catalogInsectVariantSort } from '../../../../common/sorts';

import { AuditInfoComponent, CanComponentDeactivate } from '../../../../components';

import { AppInsightsService } from '../../../../services/app-insights.service';
import {
  CatalogAdminService,
  ICatalogInsect,
  ICatalogInsectGrowthStage,
  ICatalogInsectGrowthStageUpdateRequest,
  ICatalogInsectUpdateRequest,
  ICatalogInsectVariant,
  ICatalogInsectVariantUpdateRequest,
  IInsectSynonym
} from '../../../../services/insecta';

@Component({
  selector: 'tab-insect',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    AccordionModule,
    BreadcrumbModule,
    ButtonModule,
    CardModule,
    CheckboxModule,
    ChipModule,
    ContextMenuModule,
    DatePickerModule,
    DialogModule,
    FloatLabelModule,
    FieldsetModule,
    IconFieldModule,
    InputIconModule,
    InputNumberModule,
    InputTextModule,
    PanelModule,
    SelectModule,
    TableModule,
    TabsModule,
    TagModule,
    TextareaModule,
    ToggleSwitchModule,
    TreeSelectModule,
    TreeTableModule,
    AuditInfoComponent
  ],

  templateUrl: 'tab-insects.component.html',
  styles: []
})
export class TabInsectComponent implements OnDestroy, OnChanges, OnInit, CanComponentDeactivate {
  dirtyConfirm$: ((leave: boolean) => void) | null = null;
  emptyGuid: string = `00000000-0000-0000-0000-000000000000`;

  pageVars: any = {
    isDirty: false,
    isEditing: false,
    isReady: false,
    pageId: `Admin:SiteCatalog`,
    showDirtyDlg: false
  };

  @ViewChild('searchBox') searchBox!: ElementRef<HTMLInputElement>;
  @ViewChild('dt') dt!: Table;

  @Input() currentUser: string = ``;
  @Input() catalogId: string = this.emptyGuid;
  @Input() catalogInsects: ICatalogInsect[] = [];
  @Output('isReady') isReady = new EventEmitter<boolean>();

  selectedCatalogInsect?: ICatalogInsect;

  constructor(
    private appInsights: AppInsightsService,
    private catalogService: CatalogAdminService,
    private msgService: MessageService,
    @Inject(SETTINGS) private settings: Settings
  ) {
    this.pageVars.pageId = `${settings.environment}:Admin:SiteCatalog:InsectsTab`;
    appInsights.trackPageView(this.pageVars.pageId, `/admin/sitecatalog#tabinsects`);
  }

  canDeactivate(): Promise<boolean> {
    if (!this.pageVars.isEditing) return Promise.resolve(true);

    return new Promise((resolve) => {
      this.pageVars.showDirtyDlg = true;
      const confirm$ = (decision: boolean) => {
        this.dirtyConfirm$ = null;
        resolve(decision);
      };
      this.dirtyConfirm$ = confirm$;
    });
  }

  dirtyConfirm(leave: boolean) {
    this.pageVars.showDirtyDlg = false;
    if (this.dirtyConfirm$) {
      this.dirtyConfirm$(leave);
    }
  }

  ngOnDestroy() {
    this.pageVars = {
      isReady: false
    };
    this.catalogInsects = [];
    this.selectedCatalogInsect = undefined;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['catalogInsects']) {
      if (this.searchBox !== undefined) this.searchBox.nativeElement.value = '';
      if (this.dt !== undefined) this.dt.clear();

      this.selectedCatalogInsect = undefined;
      this.catalogInsects = this.catalogInsects.map((ci) => {
        ci.growthStages = ci.growthStages.sort(catalogInsectGrowthStageSort);
        ci.variants = ci.variants.sort(catalogInsectVariantSort);
        return ci;
      });
    }
  }

  ngOnInit() {
    this.isReady.emit(false);
    this.appInsights.trackEvent(`${this.pageVars.pageId}:Init#tabinsects`);
    this.pageVars.isReady = false;

    this.selectedCatalogInsect = undefined;

    this.catalogInsects = this.catalogInsects.map((ci) => {
      ci.growthStages = ci.growthStages.sort(catalogInsectGrowthStageSort);
      ci.variants = ci.variants.sort(catalogInsectVariantSort);
      return ci;
    });
    this.pageVars.isReady = true;
    this.pageVars.isEditing = false;
    this.isReady.emit(true);
  }

  updateInsectGrowthStage(growthStage: ICatalogInsectGrowthStage) {
    const request = <ICatalogInsectGrowthStageUpdateRequest>{
      id: growthStage.id,
      leadTime: growthStage.leadTime,
      isAvailable: growthStage.isAvailable,
      isExternal: growthStage.isExternal,
      isActive: growthStage.isActive,
      actionBy: this.currentUser
    };

    firstValueFrom(this.catalogService.updateCatalogInsectGrowthStage(this.catalogId, request))
      .then(() => {
        const idx = this.selectedCatalogInsect!.growthStages.findIndex((x) => x.id === growthStage.id);
        this.selectedCatalogInsect!.growthStages[idx].action = 'UPD';
        this.selectedCatalogInsect!.growthStages[idx].actionBy = this.currentUser;
        this.selectedCatalogInsect!.growthStages[idx].actionOn = new Date();

        const insectIdx = this.catalogInsects.findIndex((x) => x.id === this.selectedCatalogInsect!.id);
        this.catalogInsects[insectIdx] = this.selectedCatalogInsect!;

        successMsg(this.msgService, 'Catalog Insect Growth Stage has been updated');
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'TabInsectComponent', action: 'UpdateInsectGrowthStage:Error' });
      });
  }

  updateInsectVariant(variant: ICatalogInsectVariant) {
    const request = <ICatalogInsectVariantUpdateRequest>{
      id: variant.id,
      leadTime: variant.leadTime,
      siteCode: variant.siteCode,
      isAvailable: variant.isAvailable,
      isExternal: variant.isExternal,
      isActive: variant.isActive,
      actionBy: this.currentUser
    };

    firstValueFrom(this.catalogService.updateCatalogInsectVariant(this.catalogId, request))
      .then(() => {
        const idx = this.selectedCatalogInsect!.variants.findIndex((x) => x.id === variant.id);
        this.selectedCatalogInsect!.variants[idx].action = 'UPD';
        this.selectedCatalogInsect!.variants[idx].actionBy = this.currentUser;
        this.selectedCatalogInsect!.variants[idx].actionOn = new Date();

        const insectIdx = this.catalogInsects.findIndex((x) => x.id === this.selectedCatalogInsect!.id);
        this.catalogInsects[insectIdx] = this.selectedCatalogInsect!;

        successMsg(this.msgService, 'Catalog Insect Variant has been updated');
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'TabInsectComponent', action: 'UpdateInsectVariant:Error' });
      });
  }
  getActiveInsectSynonyms(): IInsectSynonym[] {
    if (!this.selectedCatalogInsect?.insect?.insectSynonyms) {
      return [];
    }
    return this.selectedCatalogInsect.insect.insectSynonyms.filter((synonym) => synonym.isActive);
  }

  updatePreferredCommonName() {
    if (!this.selectedCatalogInsect) {
      return;
    }

    const request = <ICatalogInsectUpdateRequest>{
      id: this.selectedCatalogInsect.id,
      insectSynonymId: this.selectedCatalogInsect.insectSynonymId,
      actionBy: this.currentUser
    };

    firstValueFrom(this.catalogService.updateCatalogInsect(this.catalogId, request))
      .then(() => {
        this.selectedCatalogInsect!.action = 'UPD';
        this.selectedCatalogInsect!.actionBy = this.currentUser;
        this.selectedCatalogInsect!.actionOn = new Date();

        const insectIdx = this.catalogInsects.findIndex((x) => x.id === this.selectedCatalogInsect!.id);
        if (insectIdx !== -1) {
          this.catalogInsects[insectIdx] = this.selectedCatalogInsect!;
        }

        successMsg(this.msgService, 'Preferred Common Name has been updated');
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, {
          component: 'TabInsectComponent',
          action: 'UpdatePreferredCommonName:Error'
        });
      });
  }
}
